import type { ServerMetadata } from "@app/lib/actions/mcp_internal_actions/tool_definition";
import { z } from "zod";

export const GOOGLE_CHAT_TOOL_NAME = "google_chat" as const;

const googleChatScopes = [
  "https://www.googleapis.com/auth/chat.spaces.readonly",
  "https://www.googleapis.com/auth/chat.memberships.readonly",
  "https://www.googleapis.com/auth/chat.messages.readonly",
  "https://www.googleapis.com/auth/chat.messages.create",
].join(" ");

export const GOOGLE_CHAT_TOOLS_METADATA = [
  {
    name: "list_spaces",
    description:
      "List Chat spaces, group chats, and direct messages the user belongs to.",
    schema: {
      pageToken: z
        .string()
        .optional()
        .describe("Page token from a previous list_spaces call."),
      pageSize: z
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe("Maximum spaces to return (default 30, max 100)."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing Google Chat spaces",
      done: "List Google Chat spaces",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "get_space",
    description: "Get a Chat space by ID, including its name and type.",
    schema: {
      spaceId: z
        .string()
        .describe(
          "The Chat space ID, with or without the spaces/ prefix (e.g. spaces/AAA...).",
        ),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Retrieving Google Chat space",
      done: "Retrieve Google Chat space",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "list_messages",
    description: "List recent messages in a Chat space or direct message.",
    schema: {
      spaceId: z
        .string()
        .describe(
          "The Chat space ID, with or without the spaces/ prefix (e.g. spaces/AAA...).",
        ),
      pageToken: z
        .string()
        .optional()
        .describe("Page token from a previous list_messages call."),
      pageSize: z
        .number()
        .int()
        .min(1)
        .max(100)
        .optional()
        .describe("Maximum messages to return (default 30, max 100)."),
    },
    stake: "never_ask",
    displayLabels: {
      running: "Listing Google Chat messages",
      done: "List Google Chat messages",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
  {
    name: "post_message",
    description:
      "Post a message to a Chat space or direct message as the user. Only post to a space the user named.",
    schema: {
      spaceId: z
        .string()
        .describe(
          "The Chat space ID, with or without the spaces/ prefix (e.g. spaces/AAA...).",
        ),
      text: z.string().describe("The message to post, as plain text."),
    },
    stake: "medium",
    displayLabels: {
      running: "Posting Google Chat message",
      done: "Post Google Chat message",
    },
    toolCostCategory: "advanced",
    freeUsage: false,
  },
] as const;

export const GOOGLE_CHAT_SERVER = {
  serverInfo: {
    name: GOOGLE_CHAT_TOOL_NAME,
    version: "1.0.0",
    description:
      "Read Google Chat spaces and messages, and post as the connected user.",
    authorization: {
      provider: "google_drive",
      supported_use_cases: ["personal_actions", "platform_actions"],
      scope: googleChatScopes,
    },
    icon: "GoogleChatLogo",
    documentationUrl: null,
  },
  tools: GOOGLE_CHAT_TOOLS_METADATA,
} as const satisfies ServerMetadata;
